import { usePathname, useRouter } from 'next/navigation'
import type { RefObject } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { isDesktop } from 'react-device-detect'
import * as GA from '~/libs/ga'

type NewVideoItemCardActions = {
  onClickVideoDetail: (videoId: number) => void
  handlePointerEnter: () => void
  handlePointerLeave: () => void
  showHoverLayer: boolean
  hoverStatus: 'hidden' | 'enter' | 'exit'
  cardRect: DOMRect | null
  cardRef: RefObject<HTMLDivElement | null>
}

const OPEN_DELAY_MS = 700
const CLOSE_DELAY_MS = 450
const EXIT_DURATION_MS = 50

export const useNewVideoItemCardActions = (
  onGetConversion?: () => void,
): NewVideoItemCardActions => {
  const { push } = useRouter()
  const asPath = usePathname()

  const [hoverStatus, setHoverStatus] = useState<'hidden' | 'enter' | 'exit'>('hidden')
  const [cardRect, setCardRect] = useState<DOMRect | null>(null)
  const cardRef = useRef<HTMLDivElement>(null)

  const hoverEnabled = isDesktop && typeof window !== 'undefined'
  const showHoverLayer = hoverEnabled && hoverStatus !== 'hidden'

  const hoverShowTimer = useRef<number | undefined>(undefined)
  const hoverHideTimer = useRef<number | undefined>(undefined)
  const hoverExitTimer = useRef<number | undefined>(undefined)

  const screen = useMemo(() => {
    if (asPath === '/') {
      return 'stream'
    } else if (asPath?.startsWith('/search/categories')) {
      return 'category_videos'
    } else if (asPath?.startsWith('/search/result')) {
      return 'search_result'
    }
  }, [asPath])

  const clearHoverTimers = useCallback(() => {
    if (hoverShowTimer.current) {
      window.clearTimeout(hoverShowTimer.current)
      hoverShowTimer.current = undefined
    }
    if (hoverHideTimer.current) {
      window.clearTimeout(hoverHideTimer.current)
      hoverHideTimer.current = undefined
    }
    if (hoverExitTimer.current) {
      window.clearTimeout(hoverExitTimer.current)
      hoverExitTimer.current = undefined
    }
  }, [])

  const updateCardRect = useCallback(() => {
    if (!cardRef.current) return
    setCardRect(cardRef.current.getBoundingClientRect())
  }, [])

  const showHover = useCallback(() => {
    if (!hoverEnabled) return
    clearHoverTimers()
    updateCardRect()
    setHoverStatus('enter')
    onGetConversion?.()
  }, [hoverEnabled, updateCardRect, clearHoverTimers, onGetConversion])

  const scheduleHide = useCallback(() => {
    clearHoverTimers()
    if (hoverStatus === 'hidden') return
    hoverHideTimer.current = window.setTimeout(() => {
      setHoverStatus('exit')
      hoverExitTimer.current = window.setTimeout(() => {
        setHoverStatus('hidden')
      }, EXIT_DURATION_MS)
    }, CLOSE_DELAY_MS)
  }, [clearHoverTimers, CLOSE_DELAY_MS, EXIT_DURATION_MS, hoverStatus])

  const handlePointerEnter = useCallback(() => {
    if (!hoverEnabled) return
    clearHoverTimers()
    hoverShowTimer.current = window.setTimeout(showHover, OPEN_DELAY_MS)
  }, [hoverEnabled, showHover, OPEN_DELAY_MS, clearHoverTimers])

  const handlePointerLeave = useCallback(() => {
    if (!hoverEnabled) return
    scheduleHide()
  }, [hoverEnabled, scheduleHide])

  const onClickVideoDetail = useCallback(
    (videoId: number) => {
      if (screen !== undefined) {
        GA.sendEvent({
          event: 'click',
          screen,
          category: 'button',
          action: 'move_detail_page',
          label: `${videoId}`,
        })
      }
      void push(`/videos/${videoId}`)
    },
    [push, screen],
  )

  useEffect(() => {
    return () => {
      clearHoverTimers()
    }
  }, [clearHoverTimers])

  useEffect(() => {
    if (!showHoverLayer) return
    const handleLayout = () => {
      updateCardRect()
    }
    handleLayout()
    window.addEventListener('scroll', handleLayout, { passive: true })
    window.addEventListener('resize', handleLayout)
    return () => {
      window.removeEventListener('scroll', handleLayout)
      window.removeEventListener('resize', handleLayout)
    }
  }, [showHoverLayer, updateCardRect])

  return {
    onClickVideoDetail,
    handlePointerEnter,
    handlePointerLeave,
    showHoverLayer,
    hoverStatus,
    cardRect,
    cardRef,
  }
}
